// Minimal typed event emitter — no external dependencies
import type {
  ContractEventHandler,
  ContractEventName,
  ContractEventsMap,
} from "./types";

type AnyHandler = (payload: unknown) => void;

export class EventEmitter {
  private listeners = new Map<ContractEventName, Set<AnyHandler>>();

  on<K extends ContractEventName>(
    event: K,
    handler: ContractEventHandler<K>,
  ): this {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(handler as AnyHandler);
    return this;
  }

  once<K extends ContractEventName>(
    event: K,
    handler: ContractEventHandler<K>,
  ): this {
    // Unregister the wrapper BEFORE invoking the handler so a throwing handler
    // can't leak (the wrapper would otherwise remain registered and the "once"
    // listener could fire again).
    const wrapper: ContractEventHandler<K> = (payload) => {
      this.off(event, wrapper);
      handler(payload);
    };
    return this.on(event, wrapper);
  }

  off<K extends ContractEventName>(
    event: K,
    handler?: ContractEventHandler<K>,
  ): this {
    if (!handler) {
      this.listeners.delete(event);
    } else {
      this.listeners.get(event)?.delete(handler as AnyHandler);
    }
    return this;
  }

  emit<K extends ContractEventName>(
    event: K,
    payload: ContractEventsMap[K],
  ): void {
    // Snapshot the listener set before iterating so concurrent on/off calls
    // from handlers don't mutate the set mid-iteration. Wrap each handler in
    // try/catch so one throwing partner handler doesn't break delivery to
    // siblings — matches the EventTarget (browser) convention, not Node's
    // EventEmitter which propagates.
    const handlers = this.listeners.get(event);
    if (!handlers) return;
    for (const h of [...handlers]) {
      try {
        h(payload);
      } catch (err) {
        // Swallow + log; never break the emit loop. Partner code should not
        // be able to take down the SDK by throwing from an event handler.
        console.error(`[QuantumContracts] handler for "${event}" threw:`, err);
      }
    }
  }

  removeAllListeners(): void {
    this.listeners.clear();
  }
}
